import { useState } from 'react';
import { Card, Button } from '@lms/ui';
import { User, Lock, Upload, Save, UserCircle, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import type { UserGender } from '@lms/shared/types';

export function Profile() {
  const { user, token, setAuth } = useAuthStore();
  const { t } = useLanguage();

  const [formData, setFormData] = useState<{
    name: string;
    phone: string;
    gender: UserGender;
  }>(() => ({
    name: user?.name || '',
    phone: user?.phone || '',
    gender: user?.gender === 'FEMALE' ? 'FEMALE' : 'MALE',
  }));

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const updateProfileMutation = useMutation({
    mutationFn: async () => {
      let avatarUrl = user?.avatar;

      if (avatarFile) {
        const uploadRes = await apiClient.storage.upload(avatarFile);
        avatarUrl = uploadRes.url;
      }

      const updateData: {
        name: string;
        phone: string;
        gender: UserGender;
        avatar?: string;
      } = { ...formData };
      if (avatarUrl) updateData.avatar = avatarUrl;

      // If password fields are filled
      if (passwordData.newPassword) {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
          throw new Error(t(copy.profile.passwordMismatch));
        }
        if (passwordData.newPassword.length < 8) {
          throw new Error(t(copy.profile.passwordTooShort));
        }
        await apiClient.auth.changePassword({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        });
      }

      return apiClient.auth.updateProfile(updateData);
    },
    onSuccess: updatedUser => {
      if (token) setAuth(token, updatedUser);
      setSuccessMsg(t(copy.profile.successUpdate));
      setErrorMsg('');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccessMsg(''), 3000);
    },
    onError: (err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : t(copy.profile.failedUpdate));
      setSuccessMsg('');
    },
  });

  return (
    <div className="space-y-8 animate-fadeIn max-w-[800px]">
      {/* Header */}
      <section>
        <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-[0.2em] mb-2 font-label">
          <UserCircle size={14} />
          <span>
            <Bilingual {...copy.profile.administration} />
          </span>
        </div>
        <div className="flex items-center gap-4 mb-3">
          <h1 className="text-4xl font-headline font-extrabold text-on-surface tracking-tight m-0">
            <Bilingual {...copy.profile.title} />
          </h1>
        </div>
        <p className="text-lg font-body text-on-surface-variant max-w-3xl leading-relaxed">
          <Bilingual {...copy.profile.subtitle} />
        </p>
      </section>

      {errorMsg && (
        <div className="p-4 rounded-lg bg-error-container/20 border border-error/50 text-error text-sm font-medium">
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="p-4 rounded-lg bg-success/10 border border-success/30 text-success text-sm font-medium">
          {successMsg}
        </div>
      )}

      <form
        onSubmit={e => {
          e.preventDefault();
          updateProfileMutation.mutate();
        }}
        className="space-y-8"
      >
        <Card className="border border-on-surface/5 p-6">
          <h3 className="text-xl font-headline font-bold text-on-surface mb-6 flex items-center gap-3">
            <User className="text-primary" size={24} />
            <Bilingual {...copy.profile.personalData} />
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.avatar} />
              </label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-surface-container flex items-center justify-center overflow-hidden border border-outline-variant/30">
                  {avatarFile ? (
                    <img
                      src={URL.createObjectURL(avatarFile)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : user?.avatar ? (
                    <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <UserCircle size={40} className="text-on-surface-variant/50" />
                  )}
                </div>
                <label className="cursor-pointer px-4 py-2 border border-outline-variant/50 hover:bg-surface-container text-on-surface rounded-lg text-sm font-bold tracking-widest uppercase transition-colors flex items-center gap-2">
                  <Upload size={16} />
                  {t(copy.profile.changePhoto)}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setAvatarFile(file);
                    }}
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.fullName} />
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.emailDisabled} />
              </label>
              <input
                type="email"
                disabled
                value={user?.email || ''}
                className="w-full px-4 py-2 bg-surface-container border border-surface-container rounded-lg text-on-surface-variant cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.phone} />
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.gender} />
              </label>
              <select
                value={formData.gender}
                onChange={e => {
                  const gender = e.target.value;
                  if (gender === 'MALE' || gender === 'FEMALE') {
                    setFormData({ ...formData, gender });
                  }
                }}
                className="w-full px-4 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
              >
                <option value="MALE">{t(copy.profile.genderMale)}</option>
                <option value="FEMALE">{t(copy.profile.genderFemale)}</option>
              </select>
            </div>
          </div>
        </Card>

        <Card className="border border-on-surface/5 p-6">
          <h3 className="text-xl font-headline font-bold text-on-surface mb-6 flex items-center gap-3">
            <Lock className="text-primary" size={24} />
            <Bilingual {...copy.profile.securityTitle} />
          </h3>
          <p className="text-sm text-on-surface-variant mb-6">
            <Bilingual {...copy.profile.securityDesc} />
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.currentPassword} />
              </label>
              <div className="relative w-full md:w-1/2">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={e =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className="w-full pl-4 pr-10 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={
                    showCurrentPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors p-1"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.newPassword} />
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  minLength={6}
                  value={passwordData.newPassword}
                  onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full pl-4 pr-10 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors p-1"
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-label font-bold text-on-surface mb-2">
                <Bilingual {...copy.profile.confirmNewPassword} />
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  minLength={6}
                  value={passwordData.confirmPassword}
                  onChange={e =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className="w-full pl-4 pr-10 py-2 bg-surface-container-low border border-surface-container rounded-lg text-on-surface focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors p-1"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <div className="flex justify-end pt-4 border-t border-outline-variant/10">
          <Button
            type="submit"
            disabled={updateProfileMutation.isPending}
            className="flex items-center gap-2"
          >
            <Save size={18} />
            {updateProfileMutation.isPending ? t(copy.profile.btnSaving) : t(copy.profile.btnSave)}
          </Button>
        </div>
      </form>
    </div>
  );
}
